import _ from 'lodash';
import { ReportDataProvider } from './ReportDataProvider';
import {
  Metrics,
  OutdoorReportTableData,
  ReportDimension,
  ReportRecord,
  VideoReportTableData
} from 'core/report/ReportData';
import { divideValue } from './ReportDataHelper';
import { tableColumnSettings } from './reportTableColumnSettings';
import { Actor, RoleNames } from 'core';
import { isRoles } from 'core/permission/PermissionDSL';
import { ColumnDefinition } from 'components/TableColumn/TableColumn';

enum COLUMNS {
  NAME = 'name',
  IMPRES = 'impres',
  VIEW = 'adView',
  VIEW_RATE = 'viewRate',
  CPM = 'cpm',
  ACPM = 'acpm',
  FIRST_QUARTILE = 'firstQuartile',
  MID_POINT = 'midpoint',
  THIRD_QUARTILE = 'thirdQuartile',
  COMPLETE = 'complete',
  BEACON_EVENTS = 'beaconEvents',
  E_PUBLISHER_IMPRES = 'ePublisherImpres',
  AGENCY_PROFIT = 'agcProfit',
  SPENT = 'spent',
  TAGS = 'tags',
  EDITBTNS = 'editBtns'
}

export class OutdoorReportDataProvider implements ReportDataProvider {
  constructor(
    private onDimensionNameClick: (
      dimension: string,
      rowNameValue: string | number
    ) => void,
    private onDateClick: (date: string) => void
  ) {}

  get avalableDimensions() {
    return [
      ReportDimension.DAY,
      ReportDimension.AGENCY,
      ReportDimension.ADVERTISER,
      ReportDimension.ORDER,
      ReportDimension.L1_OBJECT,
      ReportDimension.L2_OBJECT,
      ReportDimension.CAMPAIGN_TYPE,
      ReportDimension.SALES_CHANNEL,
      ReportDimension.CREATIVE,
      ReportDimension.CREATIVE_SIZE,
      ReportDimension.MRT
    ];
  }

  getReportDimensions(actor: Actor | null): ReportDimension[] {
    const isSysAdmin = isRoles(RoleNames.sysAdmin).visible({ actor });
    const isAdvertiser = isRoles(
      RoleNames.adsAdmin,
      RoleNames.adsReport,
      RoleNames.adsSales
    ).visible({ actor });
    const forbidDimensions = isSysAdmin
      ? [ReportDimension.MONTH, ReportDimension.HOUR]
      : [ReportDimension.MONTH, ReportDimension.HOUR, ReportDimension.AGENCY];
    isAdvertiser && forbidDimensions.push(ReportDimension.ADVERTISER);
    if (actor?.agencyId !== 57) {
      forbidDimensions.push(ReportDimension.MRT);
    }
    return this.avalableDimensions.filter(
      (dimension) => forbidDimensions.indexOf(dimension) === -1
    );
  }

  getReportTableColumnSettings(
    allowMetrics: Metrics[],
    tableData: VideoReportTableData[],
    dimension: ReportDimension,
    currency: string
  ): ColumnDefinition[] {
    const allColumnSettings = tableColumnSettings(
      tableData,
      dimension,
      currency,
      this.onDimensionNameClick,
      this.onDateClick
    );
    const tableAllowMetrics = [...allowMetrics, COLUMNS.NAME, COLUMNS.EDITBTNS];
    if (allowMetrics.indexOf(Metrics.VIEW) !== -1) {
      allowMetrics.indexOf(Metrics.IMPRES) !== -1 &&
        tableAllowMetrics.push(COLUMNS.VIEW_RATE);
    }
    if (allowMetrics.indexOf(Metrics.SPENT) !== -1) {
      allowMetrics.indexOf(Metrics.IMPRES) !== -1 &&
        tableAllowMetrics.push(COLUMNS.CPM);
    }
    if (allowMetrics.indexOf(Metrics.SPENT) !== -1) {
      allowMetrics.indexOf(Metrics.E_PUBLISHER_IMPRES) !== -1 &&
        tableAllowMetrics.push(COLUMNS.ACPM);
    }
    dimension === ReportDimension.CAMPAIGN &&
      tableAllowMetrics.push(COLUMNS.TAGS);
    return _.compact(
      Object.values(COLUMNS).map((column) => {
        return (
          tableAllowMetrics.indexOf(column) !== -1 && allColumnSettings[column]
        );
      })
    );
  }

  getReportTableData(records: ReportRecord[]): OutdoorReportTableData[] {
    return records.map((record) => {
      return {
        key: record.dimensionName + _.get(record, 'dimensionID', 0),
        id: _.get(record, 'dimensionID', 0).toString(),
        tags: record.tags,
        extra: record.extra,
        [COLUMNS.NAME]: record.dimensionName,
        [COLUMNS.IMPRES]: record.impres,
        [COLUMNS.VIEW]: _.get(record, 'view', 0),
        [COLUMNS.VIEW_RATE]: divideValue(
          _.get(record, 'view', 0) * 100,
          _.get(record, 'impres', 0)
        ),
        [COLUMNS.ACPM]: divideValue(
          _.get(record, 'spent', 0) * 1000,
          _.get(record, 'ePublisherImpres', 0)
        ),
        [COLUMNS.CPM]: divideValue(
          _.get(record, 'spent', 0) * 1000,
          _.get(record, 'impres', 0)
        ),
        [COLUMNS.FIRST_QUARTILE]: _.get(record, 'firstQuartile', 0),
        [COLUMNS.MID_POINT]: _.get(record, 'midpoint', 0),
        [COLUMNS.THIRD_QUARTILE]: _.get(record, 'thirdQuartile', 0),
        [COLUMNS.COMPLETE]: _.get(record, 'complete', 0),
        [COLUMNS.BEACON_EVENTS]: _.get(record, 'beaconEvents', 0),
        [COLUMNS.E_PUBLISHER_IMPRES]: _.get(record, 'ePublisherImpres', 0),
        [COLUMNS.AGENCY_PROFIT]: _.get(record, 'agcProfit', 0),
        [COLUMNS.SPENT]: _.get(record, 'spent', 0)
      };
    });
  }
}
